import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { debugLog } from "@/lib/debug";
import { Lock, KeyRound, ArrowLeft, Fingerprint } from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";

function errorDetail(err, fallback) {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

function ForgotPasswordForm({ api, email, setEmail, onBack }) {
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${api}/auth/forgot-password`, { email });
      toast.success("If that account exists, a reset link has been emailed to you.");
      onBack();
    } catch (err) {
      debugLog("forgot-password request failed:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
      <p className="text-sm text-slate-600">Enter your account email and we'll send you a link to reset your password.</p>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
        <Input type="email" required className="mt-2 bg-white" placeholder="you@workplaceone.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-[#478261] hover:bg-[#3a6b51] text-white" data-testid="forgot-submit-button">
        <KeyRound className="w-4 h-4 mr-2" />{loading ? "Sending..." : "Send Reset Link"}
      </Button>
      <div className="text-center">
        <button type="button" onClick={onBack} className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2 inline-flex items-center gap-1" data-testid="back-to-login-button">
          <ArrowLeft className="w-3 h-3" />Back to sign in
        </button>
      </div>
    </form>
  );
}

function ResetPasswordForm({ api, resetToken, onDone }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await axios.post(`${api}/auth/reset-password`, { token: resetToken, password: newPassword });
      toast.success("Password updated — sign in with your new password");
      onDone();
    } catch (err) {
      toast.error(errorDetail(err, "Reset failed — request a new link"));
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
      <p className="text-sm text-slate-600">Choose a new password for your account.</p>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">New Password</Label>
        <Input type="password" required minLength={8} className="mt-2 bg-white" placeholder="Min 8 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="reset-password-input" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Confirm New Password</Label>
        <Input type="password" required minLength={8} className="mt-2 bg-white" placeholder="Repeat password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="reset-confirm-input" />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-[#478261] hover:bg-[#3a6b51] text-white" data-testid="reset-submit-button">
        <KeyRound className="w-4 h-4 mr-2" />{loading ? "Updating..." : "Update Password"}
      </Button>
    </form>
  );
}

function PasskeyLoginButton({ api, loading, setLoading, onLogin }) {
  const passkeyLogin = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${api}/auth/passkeys/login/options`, {}, { withCredentials: true });
      const credential = await startAuthentication({ optionsJSON: r.data.options });
      const v = await axios.post(`${api}/auth/passkeys/login/verify`, { state: r.data.state, credential }, { withCredentials: true });
      toast.success(`Welcome back, ${v.data.name || v.data.email}`);
      onLogin(v.data);
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        toast.info("Passkey sign-in cancelled");
      } else {
        toast.error(errorDetail(err, "Passkey sign-in failed"));
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <div className="relative py-1">
        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
        <div className="relative flex justify-center text-[11px] uppercase tracking-wider"><span className="bg-white px-2 text-slate-400">or</span></div>
      </div>
      <Button type="button" variant="outline" onClick={passkeyLogin} disabled={loading} className="w-full border-slate-300 bg-white" data-testid="passkey-login-button">
        <Fingerprint className="w-4 h-4 mr-2" />{loading ? "Waiting…" : "Sign in with a passkey"}
      </Button>
    </>
  );
}

export default function LoginPage({ api, onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const resetToken = new URLSearchParams(window.location.search).get("reset_token");
  const [mode, setMode] = useState(resetToken ? "reset" : "login"); // login | forgot | reset
  const passkeySupported = typeof window !== "undefined" && !!window.PublicKeyCredential;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${api}/auth/login`, { email, password }, { withCredentials: true });
      toast.success(`Welcome back, ${r.data.name || r.data.email}`);
      onLogin(r.data);
    } catch (err) {
      toast.error(errorDetail(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const onResetDone = () => {
    window.history.replaceState({}, "", window.location.pathname);
    setMode("login");
    setPassword("");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md border-slate-200 shadow-md" data-testid="login-card">
        <CardHeader className="items-center text-center space-y-3 m-4 py-4 border-2 border-[#478261] rounded-lg" data-testid="login-card-header">
          <img src="/workplaceone-logo.jpg" alt="WorkplaceOne" className="h-14 w-auto object-contain rounded" />
          <div>
            <div className="text-[26px] font-bold font-mono uppercase tracking-widest text-[#478261]">WP1 DeskPoint</div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {mode === "login" && (
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
                <Input type="email" required className="mt-2 bg-white" placeholder="you@workplaceone.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Password</Label>
                <Input type="password" required className="mt-2 bg-white" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#478261] hover:bg-[#3a6b51] text-white" data-testid="login-submit-button">
                <Lock className="w-4 h-4 mr-2" />{loading ? "Signing in..." : "Sign In"}
              </Button>
              <div className="text-center">
                <button type="button" onClick={() => setMode("forgot")} className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2" data-testid="forgot-password-link">
                  Forgot your password?
                </button>
              </div>
              {passkeySupported && <PasskeyLoginButton api={api} loading={loading} setLoading={setLoading} onLogin={onLogin} />}
              <p className="text-xs text-slate-500 text-center pt-2">
                Access restricted to WorkplaceOne staff accounts. Contact d.viola@workplaceone.com for an account.
              </p>
            </form>
          )}
          {mode === "forgot" && <ForgotPasswordForm api={api} email={email} setEmail={setEmail} onBack={() => setMode("login")} />}
          {mode === "reset" && <ResetPasswordForm api={api} resetToken={resetToken} onDone={onResetDone} />}
        </CardContent>
      </Card>
    </div>
  );
}
